import { flatten } from "ramda";
import { type FC, useEffect, useState } from "react";
import { titleCase } from "title-case";
import { HeaderBar, toTimeAgo, VStack } from "widgets";
import { useURLSearchParam } from "widgets/hooks";

import { useError } from "@/app-utils/graphql/useError.ts";
import { useDocumentTitle } from "@/app-utils/hooks/useDocumentTitle.ts";
import { selMessage } from "@/messages/message.ts";
import { SelMessage } from "@/messages/SelMessage.tsx";
import { FileBrowser } from "@/modules/files/components/FileBrowser.tsx";
import type { BrowsingDirectoryFragment } from "@/modules/files/graphql/files-queries.gql.ts";
import { useGetFileParentIdsQuery } from "@/modules/files/graphql/files-queries.gql.ts";
import { useFileBrowserStore } from "@/modules/files/stores/file-browser.ts";
import { getFolderFromFile, getProjectFileIdFromFolder } from "@/modules/files/utils/utils.ts";
import { notify } from "@/modules/notifications/notify.tsx";
import { ProjectColoredBadge } from "@/modules/projects/components/ProjectColoredWidgets.tsx";
import {
  type GetAccessibleProjectGroupsQuery,
  useGetAccessibleProjectGroupsQuery,
} from "@/modules/projects/graphql/projects-queries.gql.ts";
import type { GetArrayElementType } from "@/types.js";

import { type INavPanelCategory } from "../core/navigation-panel/NavigationPanel.tsx";
import PageLayout from "../core/navigation-panel/PageLayout.tsx";
import styleLayout from "../core/styles/PageLayout.module.scss";

type UserGroup = GetArrayElementType<
  NonNullable<NonNullable<GetAccessibleProjectGroupsQuery["userGroup"]>["accessibleUserGroups"]>
>;

type Project = GetArrayElementType<UserGroup["accessibleProjects"]>;

export const FilesPage: FC = () => {
  const targetFile = useURLSearchParam("file");

  const { data, error, loading } = useGetAccessibleProjectGroupsQuery({ variables: { includeStagedProjects: true } });
  const {
    data: parentFolderData,
    loading: parentFolderLoading,
    refetch,
  } = useGetFileParentIdsQuery({
    variables: { fileId: Number(targetFile) },
    skip: !targetFile,
  });
  void useError(error);

  const projectGroups: UserGroup[] | undefined | null = data?.userGroup?.accessibleUserGroups;
  const currentFolder = useFileBrowserStore((state) => state.currentFolder);

  useDocumentTitle(titleCase(`File Browser${currentFolder ? ` - ${currentFolder.project?.name}` : ""}`));

  const [initialProjectFolderId, setInitialProjectFolderId] = useState<number>();
  const [initialFolderId, setInitialFolderId] = useState<number>();

  const privateWorkspaceProjectGroup = projectGroups?.find(
    (projectGroup) =>
      projectGroup?.accessibleProjects?.length === 1 && projectGroup.accessibleProjects[0]?.isPrivateWorkspace,
  );

  useEffect(() => {
    if (targetFile) {
      refetch({ fileId: Number(targetFile) });
    }
    setInitialFolderId(undefined);
  }, [targetFile]);

  useEffect(() => {
    if (targetFile && parentFolderData) {
      const folder = parentFolderData?.collectorDirectoriesInfo?.parentFolder as BrowsingDirectoryFragment;
      const projectFolderId = getProjectFileIdFromFolder(folder);

      setInitialFolderId(getFolderFromFile(folder) ?? undefined);
      // if we have an initial path, we must wait until the project groups are available to make some preliminary checks
      if (projectGroups?.length) {
        // check existence of the target project
        const initialProjectExists = flatten(
          projectGroups.map((projectGroup) => projectGroup?.accessibleProjects),
        ).find((project) => project?.parentFileId === projectFolderId);

        if (initialProjectExists) {
          setInitialProjectFolderId(initialProjectExists.parentFileId ?? undefined);
        } else {
          // if initial target project does not exist, we show an alert and switch the initial project
          setInitialProjectFolderId(
            (privateWorkspaceProjectGroup?.accessibleProjects?.[0]?.parentFileId ||
              projectGroups[0]?.accessibleProjects?.[0]?.parentFileId) ??
              undefined,
          );

          notify.error({
            title: selMessage({ id: "filesPage.projectNotAccessible.errorTitle" }),
            children: SelMessage({ id: "filesPage.projectNotAccessible.errorMessage" }),
          });
        }
      } // ...otherwise wait until we can check the existence of the initial project
    } else if (currentFolder?.id) {
      setInitialProjectFolderId(getProjectFileIdFromFolder(currentFolder) ?? undefined);
      //! Don't set `initialFolderId` here or the project change won't happen
    } else if (privateWorkspaceProjectGroup) {
      setInitialProjectFolderId(privateWorkspaceProjectGroup.accessibleProjects?.[0]?.parentFileId ?? undefined);
    } else if (projectGroups?.length) {
      setInitialProjectFolderId(projectGroups[0]?.accessibleProjects?.[0]?.parentFileId ?? undefined);
    }
  }, [targetFile, currentFolder, privateWorkspaceProjectGroup, projectGroups, parentFolderData]);

  const categories: INavPanelCategory[] = [];
  // TODO probably makes more sense to make use of a <Redirect> to deal with all cases
  // instead of managing the "default" flag and the various query params constraints
  const redirectQueryParams = targetFile ? `?file=${targetFile}` : "";

  const privateProject = privateWorkspaceProjectGroup?.accessibleProjects?.[0];

  // Hide my places if user has no private workspaces
  if (privateProject) {
    categories.push({
      label: "My Places",
      navItems: [
        {
          label: "Private Workspace",
          route: `private`,
          search: redirectQueryParams,
          routeKey: "file-listing",
          default: initialProjectFolderId === privateProject?.parentFileId,
          pageComponent: !privateProject ? null : (
            <PageComponent project={privateProject} initialFolderId={initialFolderId} />
          ),
        },
      ],
    });
  }

  categories.push(
    ...(projectGroups ?? [])
      .filter((group) => group?.id !== privateWorkspaceProjectGroup?.id && group?.accessibleProjects?.length)
      .map((group) => ({
        label: group?.name ?? "Unknown",
        navItems:
          group?.accessibleProjects?.map((project) => ({
            label: project?.name ?? "Unknown Project",
            default: !!project?.parentFileId && initialProjectFolderId === project?.parentFileId,
            route: `${project?.id}`,
            search: redirectQueryParams,
            routeKey: "file-listing", // so that the route change does not force the page to be fully mounted again (keep grid columns state)
            pageComponent: <PageComponent project={project} initialFolderId={initialFolderId} />,
          })) ?? [],
      })),
  );

  return (
    <PageLayout navPanelCategories={categories} isLoading={loading || parentFolderLoading || !initialProjectFolderId} />
  );
};

const PageComponent: FC<{ project: Project; initialFolderId: number }> = ({ project, initialFolderId }) => (
  <VStack spacing={0} className={styleLayout.page} style={{ backgroundColor: "var(--light-background)" }}>
    <HeaderBar
      title={project.name}
      icon={<ProjectColoredBadge project={project} noLink />}
      style={{ boxShadow: "inset 0 -1px var(--ui-border)" }}
      infoAttributes={[
        {
          label: "User Group",
          description: !project.isPrivateWorkspace ? project.userGroup?.name : undefined,
        },
        { label: "Description", description: project.description },
        {
          label: "Last Activity",
          description: project.lastModified ? toTimeAgo(project.lastModified) : undefined,
        },
      ]}
    />
    <FileBrowser useStore={useFileBrowserStore} initialFolderId={initialFolderId ?? project.parentFileId} />
  </VStack>
);
